import type { TagColorType } from "components/DesignSystem/Tag/Tag";
import { colors } from "constants/colors";

export const TIMELINE_STATUS = {
  PENDING_ON_YOU: "PENDING_ON_YOU",
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS",
  UNPAID: "UNPAID",
} as const;

export type TimelineStatus =
  (typeof TIMELINE_STATUS)[keyof typeof TIMELINE_STATUS];

export const TIMELINE_STATUS_LABEL: Record<TimelineStatus, string> = {
  PENDING_ON_YOU: "Pending on you",
  COMPLETED: "Completed",
  IN_PROGRESS: "In progress",
  UNPAID: "Pending payment",
};

export const TIMELINE_LEGEND_FILTER = {
  ...TIMELINE_STATUS,
  OVERDUE: "OVERDUE",
} as const;

export type TimelineLegendFilter =
  (typeof TIMELINE_LEGEND_FILTER)[keyof typeof TIMELINE_LEGEND_FILTER];

export const TIMELINE_LEGEND_ORDER: TimelineLegendFilter[] = [
  TIMELINE_LEGEND_FILTER.COMPLETED,
  TIMELINE_LEGEND_FILTER.IN_PROGRESS,
  TIMELINE_LEGEND_FILTER.OVERDUE,
  TIMELINE_LEGEND_FILTER.UNPAID,
  TIMELINE_LEGEND_FILTER.PENDING_ON_YOU,
];

export const TIMELINE_LEGEND_LABEL: Record<TimelineLegendFilter, string> = {
  COMPLETED: "Completed",
  IN_PROGRESS: "In progress",
  OVERDUE: "Overdue",
  UNPAID: "Unpaid",
  PENDING_ON_YOU: "Pending on you",
};

export const TIMELINE_LEGEND_NOUN: Record<
  TimelineLegendFilter,
  { singular: string; plural: string }
> = {
  COMPLETED: { singular: "completed filing", plural: "completed filings" },
  IN_PROGRESS: {
    singular: "filing in progress",
    plural: "filings in progress",
  },
  OVERDUE: { singular: "overdue filing", plural: "overdue filings" },
  UNPAID: { singular: "unpaid filing", plural: "unpaid filings" },
  PENDING_ON_YOU: {
    singular: "filing pending on you",
    plural: "filings pending on you",
  },
};

export const TIMELINE_LEGEND_FILTER_TOOLTIP = "Click to filter";

export const TIMELINE_DETAIL_HINT = "Click on a filing to view details";

export const TIMELINE_PENDING_DOT_COLOR = "#F59E0B";

export const TIMELINE_STATUS_COLOR: Record<TimelineStatus, string> = {
  PENDING_ON_YOU: TIMELINE_PENDING_DOT_COLOR,
  COMPLETED: colors.dark_green[40],
  IN_PROGRESS: colors.blue[70],
  UNPAID: colors.white,
};

export const TIMELINE_OVERDUE_COLOR = colors.red[40];

// A filing already with Inkle needs no nudge, so its dot stays calm.
export const TIMELINE_NON_PULSING_STATUSES: TimelineStatus[] = [
  TIMELINE_STATUS.COMPLETED,
  TIMELINE_STATUS.IN_PROGRESS,
];

export const TIMELINE_STATUS_TAG_TYPE: Record<TimelineStatus, TagColorType> = {
  PENDING_ON_YOU: "orange",
  COMPLETED: "green",
  IN_PROGRESS: "blue",
  UNPAID: "orange",
};

export const TIMELINE_DETAIL_STATUS_COLOR: Record<TimelineStatus, string> = {
  PENDING_ON_YOU: colors.orange[70],
  COMPLETED: colors.dark_green.DEFAULT,
  IN_PROGRESS: colors.blue[70],
  UNPAID: colors.orange[70],
};

export const TIMELINE_AXIS_ELAPSED_COLOR = "#94A3B8";
export const TIMELINE_UNPAID_BORDER_COLOR = colors.neutral[50];

export const TIMELINE_CART_STATUS = {
  IN_CART: "IN_CART",
  SAVED_FOR_LATER: "SAVED_FOR_LATER",
  FILED_EXTERNALLY: "FILED_EXTERNALLY",
} as const;

export type TimelineCartStatus =
  (typeof TIMELINE_CART_STATUS)[keyof typeof TIMELINE_CART_STATUS];

export const TIMELINE_CART_STATUS_SUFFIX: Record<TimelineCartStatus, string> = {
  IN_CART: "Added to cart",
  SAVED_FOR_LATER: "Saved for later",
  FILED_EXTERNALLY: "Filed externally",
};

export const OVERDUE_LABEL = "Overdue";
export const OVERDUE_PREVIOUS_YEARS_TOOLTIP =
  "Overdue filings across previous years";
export const OVERDUE_NEXT_YEARS_TOOLTIP = "Overdue filings across next years";

export const TIMELINE_FILED_ON_LABEL = "Filed on";
export const TIMELINE_DEADLINE_LABEL = "Deadline";

export const TIMELINE_EMPTY_TITLE = "Nothing to show here yet";
export const TIMELINE_JUMP_YEAR_PREFIX = "Go to";
export const TIMELINE_EMPTY_SUBTEXT =
  "Filing timeline will start showing up here once they are identified";

export const TIMELINE_CLUSTER_THRESHOLD = 0.028;

export const TIMELINE_NAME_ROW_BOTTOM = 85;

export const TIMELINE_CHIP_BOTTOM = 32;

export const TIMELINE_NAMES_PER_MARKER = 3;

export const TIMELINE_LABEL_WIDTH = 88;

// Matches the 1.5rem inset in getTrackPosition.
export const TIMELINE_TRACK_INSET_PX = 24;

// Half a date label (~40px) + half "Today" (~36px) + a small gap.
export const TIMELINE_TODAY_OVERLAP_PX = 42;

export const TIMELINE_SWEEP_START_MS = 220;
export const TIMELINE_SWEEP_DURATION_MS = 850;

export const TIMELINE_LABEL_LEAD_MS = 60;
export const TIMELINE_LABEL_STAGGER_MS = 45;

export const SKELETON_SHOW_DELAY_MS = 150;
export const SKELETON_MIN_VISIBLE_MS = 400;

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
